// Renders a single non-streaming message bubble. Streaming / in-progress
// messages are rendered by <SpinnerMessage> instead — that branching lives
// in ThreadBody so each leaf stays single-purpose.

import { Fragment, memo, useState, type ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { Check, Copy01 } from '@kula-ai/icons-core';
import { MESSAGE_ENTER_DURATION_MS, messageEnter } from '../../constants';
import { isUserMessage } from '../../utils/messageUtils';
import { downloadAttachment } from '../../../api/messages';
import type { MessageAttachment } from '../../../types/transcript';
import { BlockRenderer } from '../../../components/blocks/BlockRenderer';
import { EmailComposeGroup } from '../../../components/blocks/emailCompose/EmailComposeGroup';
import { parseServerContent } from '../../../components/composer/mentionUtils';
import type {
  ActionTrailStep,
  BlockOutput,
  EmailComposeBlockData,
} from '../../../types';
import { StepTimeline } from '../StepTimeline';
import type { MessageProps } from './Message.types';

// Slide-in animation applied only when the message is freshly appended.
// CSS animations are one-shot per DOM node, so leaving `new_message` set to
// `true` on subsequent renders is harmless — the keyframes won't replay.
const animationStyles = (animate: boolean) =>
  animate
    ? {
        animation: `${messageEnter} ${MESSAGE_ENTER_DURATION_MS}ms ease-out both`,
      }
    : undefined;

interface BubbleProps {
  content: string;
  animate: boolean;
}

// Inline pill for an @-mention parsed out of message content. Visual
// match-up to the chip rendered inside the composer so a tag looks the
// same whether the user is drafting it or reading it back.
interface MentionChipProps {
  name: string;
  entity: string;
}

const MentionChip = ({ name, entity }: MentionChipProps) => (
  <Box
    as="span"
    display="inline-block"
    px="6px"
    mx="1px"
    borderRadius="6px"
    bg="primary-50"
    color="primary-700"
    fontWeight={500}
    title={entity}
    sx={{ whiteSpace: 'nowrap', verticalAlign: 'baseline' }}
  >
    @{name}
  </Box>
);

// Walk parsed tokens into React children. Text segments keep their
// whitespace (the surrounding <Text> uses `whitespace-pre-wrap`), mention
// tokens become atomic chips.
const renderTokens = (content: string): ReactNode => {
  const tokens = parseServerContent(content);
  if (tokens.length === 0) return content;
  return tokens.map((token, idx) => {
    if (token.type === 'text') {
      return <Fragment key={idx}>{token.text}</Fragment>;
    }
    return (
      <MentionChip
        key={idx}
        name={token.mention.name}
        entity={token.mention.entity}
      />
    );
  });
};

// Agentforge chip prompts append structured context markers to the user's
// outgoing turn so the agent can route on them without making the user
// re-supply IDs. The convention is `<ctx:KEY=VALUE>`, usually
// `<ctx:ids=24778, 21173, ...>` after a multi-row "Add to pipeline" chip
// click. Those markers are for the LLM; rendering them verbatim turns the
// chat bubble into a wall of comma-separated numbers. Strip them out for
// display and surface only a quiet "+N <key>" hint underneath so the user
// can still tell context was carried forward.
const CTX_MARKER_RE = /\s*<ctx:([a-z_][a-z0-9_]*)=([^>]*)>\s*/gi;

interface CtxMarker {
  key: string;
  count: number;
}

const splitUserContent = (
  raw: string
): { visible: string; markers: CtxMarker[] } => {
  const markers: CtxMarker[] = [];
  const visible = raw
    .replace(CTX_MARKER_RE, (_, key: string, value: string) => {
      const items = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      markers.push({ key: key.toLowerCase(), count: items.length });
      return '';
    })
    .trim();
  return { visible, markers };
};

// Human-readable size, e.g. "12 KB". Undefined size renders nothing.
const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// A downloadable attachment pill under a user bubble. Clicking fetches the
// bytes through the authenticated client and triggers a browser download.
const AttachmentChip = ({
  sessionId,
  attachment,
}: {
  sessionId: string;
  attachment: MessageAttachment;
}) => {
  const [busy, setBusy] = useState(false);
  const size = formatBytes(attachment.size);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadAttachment(sessionId, attachment);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Flex
      as="button"
      onClick={onClick}
      align="center"
      gap="6px"
      px="10px"
      py="6px"
      borderRadius="10px"
      bg="secondary-50"
      border="1px solid"
      borderColor="secondary-200"
      maxW="80%"
      cursor={busy ? 'default' : 'pointer'}
      opacity={busy ? 0.6 : 1}
      _hover={{ bg: 'secondary-100' }}
      title={`Download ${attachment.filename}`}
    >
      <Box
        as="span"
        w="14px"
        h="14px"
        flexShrink={0}
        color="secondary-500"
        aria-hidden
      >
        <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
          <path
            d="M8 1.5v8m0 0L4.5 6M8 9.5 11.5 6M2.5 11.5v1a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-1"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
      <Text
        variant="span"
        className="text-secondary-700 text-[13px]"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {busy ? 'Downloading…' : attachment.filename}
      </Text>
      {!busy && size && (
        <Text variant="span" className="text-secondary-400 text-[11px]">
          {size}
        </Text>
      )}
    </Flex>
  );
};

interface UserBubbleProps extends BubbleProps {
  sessionId: string;
  attachments?: MessageAttachment[];
}

// Right-aligned, soft-gray pill — the user's outgoing turn.
const UserBubble = ({
  content,
  animate,
  sessionId,
  attachments,
}: UserBubbleProps) => {
  const { visible, markers } = splitUserContent(content);
  return (
    <Flex
      direction="column"
      align="flex-end"
      gap="4px"
      sx={animationStyles(animate)}
    >
      <Box bg="secondary-50" borderRadius="12px" p={'10px 12px'} maxW="80%">
        <Text variant="p" className="text-secondary-700 whitespace-pre-wrap">
          {renderTokens(visible || content)}
        </Text>
      </Box>
      {attachments?.map((att) => (
        <AttachmentChip key={att.id} sessionId={sessionId} attachment={att} />
      ))}
      {markers.length > 0 && (
        <Flex gap="4px">
          {markers.map((m) => (
            <Text
              key={m.key}
              variant="span"
              className="text-secondary-400 text-[11px]"
            >
              +{m.count} {m.key}
            </Text>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

// Render blocks, collapsing consecutive email composers into one
// EmailComposeGroup (header + "Send all") so several per-candidate drafts read
// as one batch. Everything else renders one-per-BlockRenderer as before.
const renderBlocks = (blocks: BlockOutput[]): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let i = 0;
  while (i < blocks.length) {
    if (blocks[i].type === 'emailCompose') {
      const start = i;
      const drafts: EmailComposeBlockData[] = [];
      while (i < blocks.length && blocks[i].type === 'emailCompose') {
        drafts.push(
          (blocks[i] as Extract<BlockOutput, { type: 'emailCompose' }>).data
        );
        i++;
      }
      nodes.push(<EmailComposeGroup key={`eg-${start}`} drafts={drafts} />);
    } else {
      nodes.push(<BlockRenderer key={i} output={blocks[i]} />);
      i++;
    }
  }
  return nodes;
};

interface AssistantBubbleProps {
  content: string;
  blocks?: BlockOutput[];
  actionTrail?: ActionTrailStep[];
  animate: boolean;
  createdAt?: string;
}

// "2:34 PM" — same-day turns don't need the date; hover shows the full
// timestamp via the title attribute.
const formatTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

// The copyable text of an assistant turn: its prose plus any text blocks.
const copyableText = (content: string, blocks?: BlockOutput[]): string => {
  if (!blocks || blocks.length === 0) return content;
  const parts = blocks
    .filter(
      (b): b is Extract<BlockOutput, { type: 'text' }> => b.type === 'text'
    )
    .map((b) => b.data.content);
  return parts.join('\n\n') || content;
};

// Hover-reveal meta row under an assistant turn: copy + timestamp. Copy is
// table stakes for an answer people paste into ATS notes and emails.
const AssistantMeta = ({
  text,
  createdAt,
}: {
  text: string;
  createdAt?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!text) return;
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const time = formatTime(createdAt);
  return (
    <Flex
      align="center"
      gap="8px"
      mt="4px"
      opacity={0}
      transition="opacity 0.15s ease"
      _groupHover={{ opacity: 1 }}
    >
      {text && (
        <Box
          as="button"
          type="button"
          onClick={copy}
          aria-label="Copy reply"
          className="inline-flex items-center gap-1 text-[11px] text-secondary-400 hover:text-secondary-700 cursor-pointer"
          data-test-id="agentforge-copy-message"
        >
          {copied ? (
            <Check size={12} color="currentColor" />
          ) : (
            <Copy01 size={12} color="currentColor" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Box>
      )}
      {time && (
        <Text
          variant="span"
          className="text-[11px] text-secondary-400"
          title={createdAt}
        >
          {time}
        </Text>
      )}
    </Flex>
  );
};

// Left-aligned, transparent — the assistant's response. The step
// timeline (routing, context load, tool calls, compose steps) renders
// ABOVE the blocks/content, collapsed by default to "Completed N steps".
// Click to expand and see the post-mortem of what happened during the
// turn. ActionTrailStep shape is identical to TimelineStep, so the
// values pass straight through.
const AssistantBubble = ({
  content,
  blocks,
  actionTrail,
  animate,
  createdAt,
}: AssistantBubbleProps) => {
  const hasBlocks = !!blocks && blocks.length > 0;
  const hasTrail = !!actionTrail && actionTrail.length > 0;
  return (
    <Flex justify="flex-start" sx={animationStyles(animate)} role="group">
      <Box maxW="100%" w={hasBlocks ? '100%' : undefined}>
        {hasTrail && (
          <StepTimeline
            steps={actionTrail as ActionTrailStep[]}
            streaming={false}
          />
        )}
        {hasBlocks ? (
          <Flex direction="column" gap="12px">
            {renderBlocks(blocks)}
          </Flex>
        ) : (
          <Text variant="p" className="text-secondary-900 whitespace-pre-wrap">
            {renderTokens(content)}
          </Text>
        )}
        <AssistantMeta
          text={copyableText(content, blocks)}
          createdAt={createdAt}
        />
      </Box>
    </Flex>
  );
};

const MessageInner = ({ message }: MessageProps) => {
  const animate = message.new_message;
  if (isUserMessage(message)) {
    return (
      <UserBubble
        content={message.content}
        animate={animate}
        sessionId={message.session_id}
        attachments={
          message.metadata?.attachments as MessageAttachment[] | undefined
        }
      />
    );
  }
  return (
    <AssistantBubble
      content={message.content}
      blocks={message.blocks}
      actionTrail={message.actionTrail}
      animate={animate}
      createdAt={message.created_at}
    />
  );
};

// Memoised so typing in the composer (which re-renders the orchestrator)
// does not invalidate already-painted turns.
export const Message = memo(MessageInner);
